import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  Typography,
  Link,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { fetchPendingLegalDocuments, acceptLegalDocuments } from 'src/redux/identity'

const LegalDocumentType = {
  TermsOfService: 1,
  PrivacyPolicy: 2,
}

const documentNames = {
  [LegalDocumentType.TermsOfService]: 'Terms of Service',
  [LegalDocumentType.PrivacyPolicy]: 'Privacy Policy',
}

const documentRoutes = {
  [LegalDocumentType.TermsOfService]: '/tac',
  [LegalDocumentType.PrivacyPolicy]: '/privacy',
}

const AcceptLegalDocumentsModal = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const { signedIn, pendingLegalDocuments = [], pendingLegalDocumentsError, acceptingLegalDocuments } = useSelector(state => state.identity)

  const hiddenRoutes = ['/tac', '/privacy']
  if (hiddenRoutes.includes(location.pathname)) {
    return null
  }

  useEffect(() => {
    if (signedIn) {
      dispatch(fetchPendingLegalDocuments())
    }
  }, [signedIn, dispatch])

  const handleAccept = () => {
    dispatch(acceptLegalDocuments())
  }

  if (!signedIn) {
    return null
  }

  const dialogProps = {
    open: true,
    disableEscapeKeyDown: true,
    fullScreen,
    fullWidth: true,
    maxWidth: 'sm',
    sx: { zIndex: theme.zIndex.modal + 2 },
    slotProps: {
      backdrop: {
        sx: { backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.3)' },
      },
    },
  }

  const titleSx = {
    color: 'primary.main',
    pt: 3,
    pb: 1,
    px: 3,
  }

  const dividerSx = {
    bgcolor: 'primary.main',
    height: '1px',
    borderRadius: '8px',
    mx: 3,
    width: '40%',
  }

  if (pendingLegalDocumentsError) {
    return (
      <Dialog {...dialogProps}>
        <DialogTitle sx={titleSx}>
          <Typography variant='h6'>Updated Legal Documents</Typography>
        </DialogTitle>
        <Divider sx={dividerSx} />
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant='body1'>
            Something went wrong loading the terms. Please try again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.100', px: 3, py: 2 }}>
          <Button variant='contained' onClick={() => dispatch(fetchPendingLegalDocuments())}>
            Retry
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (pendingLegalDocuments.length === 0) {
    return null
  }

  return (
      <Dialog {...dialogProps}>
        <DialogTitle sx={titleSx}>
          <Typography variant='h6'>Updated Legal Documents</Typography>
        </DialogTitle>
        <Divider sx={dividerSx} />
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Typography variant='body1' sx={{ mb: 1 }}>
            We've made updates to clarify how we use third-party service providers and reflect upcoming capabilities on MyStethi.
          </Typography>
          <Typography variant='body1'>
            Please review and accept the following to continue:
          </Typography>
          <List disablePadding>
            {pendingLegalDocuments.map(doc => (
              <ListItem key={doc.id} disableGutters>
                <ListItemText
                  primary={
                    <Link href={documentRoutes[doc.documentType]} target='_blank' rel='noopener noreferrer' variant='body1'>
                      {documentNames[doc.documentType] || `Document ${doc.documentType}`}
                    </Link>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.100', px: 3, py: 2 }}>
          <Button
            variant='contained'
            fullWidth
            onClick={handleAccept}
            disabled={acceptingLegalDocuments}
          >
            I Accept
          </Button>
        </DialogActions>
      </Dialog>
  )
}

export default AcceptLegalDocumentsModal
